import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { BonusTitle } from '@atlas/components';
import { CasinoApi, IntlManager, NotificationManager, SiteConfigManager } from '@atlas/core';
import { BasePopup, FreeSpinDetails, Icon, Loader } from '@atlas/ui';

import history from 'components/Layout/history';

const FreeSpinDetailsPopup = ({
  data: {
    freeSpin,
    imageUrl,
    linkToGame,
    localizedInfoByLang,
    onActivateClick,
    onCancelClick,
    onDepositClick,
  },
  id,
}) => {
  const {
    createTime,
    currency,
    expirationTime: apiExpirationTime,
    freeSpin: freeSpinData,
    freeSpinAmount,
    freeSpinSubType,
    status,
  } = freeSpin;
  const { casinoLimit, numberOfSpins, timeToUse } = freeSpinData;

  const dateFormat = SiteConfigManager.getConfig('config.config.date.dateFormat', 'DD.MM.YYYY');
  const timeFormat = SiteConfigManager.getConfig('config.config.date.timeFormat', 'HH:mm');
  const [gameName, setGameName] = useState('-');
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Timer logic:
   * - For DEPOSIT freespins: use expirationTime from API (backend handles the logic)
   * - For REGULAR freespins: calculate from createTime + timeToUse
   */
  const isDepositType = freeSpinSubType === 'DEPOSIT';
  const endTime = isDepositType && apiExpirationTime ? apiExpirationTime : createTime + timeToUse;

  const expirationTime = useMemo(() => {
    const formattedDate = dayjs(endTime).format(dateFormat);
    const formattedTime = dayjs(endTime).format(timeFormat);

    return `${formattedDate} ${formattedTime}`;
  }, [dateFormat, endTime, timeFormat]);

  useEffect(() => {
    const gameId = casinoLimit.gameIds[0];

    CasinoApi.fetchGameDetailsV2({ gameId })
      .then(res => {
        setGameName(res.game.name);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  const handleClosePopup = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const handleButtonClick = useCallback(() => {
    handleClosePopup();
    history.push(linkToGame);
  }, [handleClosePopup, linkToGame]);

  const handleDepositClickWithClose = useCallback(
    e => {
      handleClosePopup();
      if (onDepositClick) onDepositClick(e);
    },
    [handleClosePopup, onDepositClick],
  );

  const handleActivateClickWithClose = useCallback(
    e => {
      handleClosePopup();
      if (onActivateClick) onActivateClick(e);
    },
    [handleClosePopup, onActivateClick],
  );

  const handleCancelClickWithClose = useCallback(
    e => {
      handleClosePopup();
      if (onCancelClick) onCancelClick(e);
    },
    [handleClosePopup, onCancelClick],
  );

  return (
    <BasePopup
      className={cx('freespin-details-popup', { 'freespin-details-popup--loading': isLoading })}
    >
      <button className="close-button" onClick={handleClosePopup} type="button">
        <Icon height={16} name="close" width={16} />
      </button>
      {isLoading ? (
        <Loader />
      ) : (
        <FreeSpinDetails
          amount={IntlManager.formatMoney(freeSpinAmount)}
          currencies={[currency]}
          description={localizedInfoByLang.description}
          expirationTime={expirationTime}
          freeSpinSubType={freeSpinSubType}
          gameName={gameName}
          handleButtonClick={handleButtonClick}
          imageUrl={imageUrl}
          name={
            <BonusTitle
              name={localizedInfoByLang.name}
              nameHighlighted={localizedInfoByLang.nameHighlighted}
            />
          }
          number={numberOfSpins}
          onActivateClick={handleActivateClickWithClose}
          onCancelClick={handleCancelClickWithClose}
          onDepositClick={handleDepositClickWithClose}
          status={status}
          timerTime={endTime}
        />
      )}
    </BasePopup>
  );
};

FreeSpinDetailsPopup.propTypes = {
  data: PropTypes.shape({
    freeSpin: PropTypes.shape({
      createTime: PropTypes.number,
      currency: PropTypes.string,
      expirationTime: PropTypes.number,
      freeSpin: PropTypes.shape({
        casinoLimit: PropTypes.shape({
          gameIds: PropTypes.arrayOf(PropTypes.string),
        }),
        localizedImageInfoList: PropTypes.arrayOf(
          PropTypes.shape({
            imageLink: PropTypes.string,
          }),
        ),
        numberOfSpins: PropTypes.number,
        timeToUse: PropTypes.number,
      }),
      freeSpinAmount: PropTypes.number,
      freeSpinSubType: PropTypes.oneOf(['REGULAR', 'DEPOSIT']),
      status: PropTypes.string,
    }),
    imageUrl: PropTypes.string,
    linkToGame: PropTypes.string,
    localizedInfoByLang: PropTypes.shape({
      description: PropTypes.string,
      name: PropTypes.string,
      nameHighlighted: PropTypes.string,
    }),
    onActivateClick: PropTypes.func,
    onCancelClick: PropTypes.func,
    onDepositClick: PropTypes.func,
  }).isRequired,
  id: PropTypes.number.isRequired,
};

export default FreeSpinDetailsPopup;
